// src/Profile/components/OverViewsPage/OverviewTabsChart/OverviewTabsChart.jsx
"use client";

import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import "./OverviewTabsChart.css";
import { useSelector } from "react-redux";

import useOverviewSeries from "@/Profile/hooks/useOverviewSeries";
import { selectVitalSignals } from "@/Profile/redux/selectors/vitalSignsSelectors";
import { selectEffectiveUserId } from "../../../../sous-profile/redux/selectors/sousProfileSelectors";

const timeFilters = ["ALL", "1M", "6M", "1Y"];

const FILTER_LABEL_KEY = {
  ALL: "all",
  "1M": "oneMonth",
  "6M": "sixMonths",
  "1Y": "oneYear",
};

const toNumber = (value) => {
  const n = Number(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
};

const dateOfMetric = (item) =>
  new Date(
    item?.measuredAt ||
      item?.createdAt ||
      item?.addedAt ||
      item?.date ||
      0
  ).getTime();

const formatDateLabel = (dateLike) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return "";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(d);
};

const makeCustomKey = (label, unit) => {
  return `${String(label || "Métrique").trim()}__${String(unit || "").trim()}`;
};

const filterByTime = (items, timeKey) => {
  if (timeKey === "ALL") return items;

  const now = new Date();
  const from = new Date(now);

  if (timeKey === "1M") {
    from.setMonth(from.getMonth() - 1);
  }

  if (timeKey === "6M") {
    from.setMonth(from.getMonth() - 6);
  }

  if (timeKey === "1Y") {
    from.setFullYear(from.getFullYear() - 1);
  }

  return items.filter((item) => {
    const d = new Date(item?.measuredAt || item?.createdAt || item?.date || 0);
    return d >= from && d <= now;
  });
};

const makeSmoothPath = (getX, getY, arr) => {
  const pts = arr
    .map((v, i) => ({ v, i }))
    .filter((p) => typeof p.v === "number");

  if (pts.length < 2) return "";

  let d = `M ${getX(pts[0].i)} ${getY(pts[0].v)}`;

  for (let k = 1; k < pts.length; k++) {
    const i1 = pts[k - 1].i;
    const y1 = getY(pts[k - 1].v);

    const i2 = pts[k].i;
    const y2 = getY(pts[k].v);

    const x1 = getX(i1);
    const x2 = getX(i2);

    const cp1x = x1 + (x2 - x1) * 0.4;
    const cp2x = x2 - (x2 - x1) * 0.4;

    d += ` C ${cp1x} ${y1}, ${cp2x} ${y2}, ${x2} ${y2}`;
  }

  return d;
};

const Chart = ({ tab, timeKey, getSeries, normalZone }) => {
  const { t } = useTranslation();

  const mainColor = "#006eea";
  const diaColor = "#6B7280";

  const { labels, min, max, points, sys, dia, count } = useMemo(() => {
    const s = getSeries(tab.key, timeKey) || {};

    if (tab.key === "bloodPressure") {
      return {
        labels: s.labels || [],
        min: s.min,
        max: s.max,
        count: s.count || 0,
        sys: s.sys || [],
        dia: s.dia || [],
      };
    }

    return {
      labels: s.labels || [],
      min: s.min,
      max: s.max,
      count: s.count || 0,
      points: s.points || [],
    };
  }, [tab.key, timeKey, getSeries]);

  const width = 800;
  const height = 260;

  const margin = {
    top: 20,
    right: 40,
    bottom: 40,
    left: 60,
  };

  const chartW = width - margin.left - margin.right;
  const chartH = height - margin.top - margin.bottom;

  const yMin = Number.isFinite(min) ? min : 0;
  const yMax = Number.isFinite(max) && max !== min ? max : yMin + 1;

  const getX = (i) =>
    margin.left + (i * chartW) / Math.max(1, labels.length - 1);

  const getY = (v) =>
    margin.top + ((yMax - v) / (yMax - yMin)) * chartH;

  const gridCount = 4;
  const gridStep = (yMax - yMin) / gridCount || 1;

  const gridLines = Array.from({ length: gridCount + 1 }, (_, i) =>
    Math.round((yMin + i * gridStep) * 100) / 100
  );

  const xticks = useMemo(() => {
    const n = labels.length;

    if (!n) return [];

    const maxTicks = Math.min(6, n);
    const step = Math.max(1, Math.floor(n / maxTicks));
    const ticks = [];

    for (let i = 0; i < n; i += step) {
      ticks.push({
        i,
        text: labels[i],
      });
    }

    if (ticks[ticks.length - 1]?.i !== n - 1) {
      ticks.push({
        i: n - 1,
        text: labels[n - 1],
      });
    }

    return ticks;
  }, [labels]);

  const [tip, setTip] = useState({
    visible: false,
    x: 0,
    y: 0,
    lines: [],
    idx: -1,
    date: "",
  });

  const showTip = (idx, ev, lines) => {
    const svg = ev.currentTarget.closest("svg");
    const wrap = ev.currentTarget.closest(".otc-canvas");

    const srect = svg.getBoundingClientRect();
    const wrect = wrap.getBoundingClientRect();

    const x = (getX(idx) / width) * srect.width + (srect.left - wrect.left);

    const ys = lines
      .map((l) => (typeof l.value === "number" ? getY(l.value) : null))
      .filter((v) => typeof v === "number");

    const y =
      ys.reduce((a, b) => a + b, 0) / (ys.length || 1) +
      (srect.top - wrect.top);

    setTip({
      visible: true,
      x,
      y,
      lines,
      idx,
      date: labels[idx] || "",
    });
  };

  const hideTip = () =>
    setTip((t0) => ({
      ...t0,
      visible: false,
    }));

  const pathSingle = points ? makeSmoothPath(getX, getY, points) : "";
  const pathSYS = sys ? makeSmoothPath(getX, getY, sys) : "";
  const pathDIA = dia ? makeSmoothPath(getX, getY, dia) : "";

  const nz = normalZone?.[tab.key];

  return (
    <div
      className="otc-canvas"
      role="img"
      aria-label={t("overviewTabsChart.chartAriaLabel")}
    >
      {count === 0 && (
        <div className="otc-empty">
          No data available for this metric.
        </div>
      )}

      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        {gridLines.map((val) => (
          <g key={`gy-${val}`}>
            <line
              x1={margin.left}
              x2={width - margin.right}
              y1={getY(val)}
              y2={getY(val)}
              stroke="#e2e8f0"
              strokeWidth="1"
            />

            <text
              x={margin.left - 10}
              y={getY(val) + 4}
              textAnchor="end"
              fontSize="12"
              fill="#94a3b8"
            >
              {val}
            </text>
          </g>
        ))}

        {nz && tab.key !== "bloodPressure" && (
          <rect
            x={margin.left}
            y={getY(nz.max)}
            width={chartW}
            height={getY(nz.min) - getY(nz.max)}
            fill="#22c55e"
            fillOpacity="0.08"
          />
        )}

        {tab.key !== "bloodPressure" ? (
          <>
            <path
              d={pathSingle}
              stroke={mainColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {points?.map((v, i) =>
              typeof v === "number" ? (
                <g key={`p-${i}`}>
                  <circle
                    cx={getX(i)}
                    cy={getY(v)}
                    r="10"
                    fill="transparent"
                    onMouseEnter={(e) =>
                      showTip(i, e, [
                        {
                          label: tab.label,
                          value: v,
                          unit: tab.unit,
                          color: mainColor,
                        },
                      ])
                    }
                    onMouseLeave={hideTip}
                    style={{ cursor: "pointer" }}
                  />

                  <circle
                    cx={getX(i)}
                    cy={getY(v)}
                    r="4"
                    fill={mainColor}
                    stroke="white"
                    strokeWidth="2"
                    style={{
                      filter: "drop-shadow(0 2px 6px rgba(47,117,148,.4))",
                      pointerEvents: "none",
                    }}
                  />
                </g>
              ) : null
            )}
          </>
        ) : (
          <>
            <path
              d={pathSYS}
              stroke={mainColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            <path
              d={pathDIA}
              stroke={diaColor}
              strokeWidth="3"
              fill="none"
              strokeDasharray="8 6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />

            {labels.map((_, i) => {
              const hasSYS = typeof sys?.[i] === "number";
              const hasDIA = typeof dia?.[i] === "number";

              if (!hasSYS && !hasDIA) return null;

              const lines = [];

              if (hasSYS) {
                lines.push({
                  label: "SYS",
                  value: sys[i],
                  unit: "mmHg",
                  color: mainColor,
                });
              }

              if (hasDIA) {
                lines.push({
                  label: "DIA",
                  value: dia[i],
                  unit: "mmHg",
                  color: diaColor,
                });
              }

              return (
                <g key={`bp-${i}`}>
                  <circle
                    cx={getX(i)}
                    cy={getY(hasSYS ? sys[i] : dia[i])}
                    r="12"
                    fill="transparent"
                    onMouseEnter={(e) => showTip(i, e, lines)}
                    onMouseLeave={hideTip}
                    style={{ cursor: "pointer" }}
                  />

                  {hasSYS && (
                    <circle
                      cx={getX(i)}
                      cy={getY(sys[i])}
                      r="4"
                      fill={mainColor}
                      stroke="white"
                      strokeWidth="2"
                      style={{
                        filter: "drop-shadow(0 2px 6px rgba(47,117,148,.4))",
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {hasDIA && (
                    <circle
                      cx={getX(i)}
                      cy={getY(dia[i])}
                      r="4"
                      fill={diaColor}
                      stroke="white"
                      strokeWidth="2"
                      style={{
                        filter: "drop-shadow(0 2px 6px rgba(107,114,128,.4))",
                        pointerEvents: "none",
                      }}
                    />
                  )}
                </g>
              );
            })}
          </>
        )}

        {xticks.map((tx) => (
          <text
            key={`xt-${tx.i}`}
            x={getX(tx.i)}
            y={height - 10}
            textAnchor="middle"
            fontSize="12"
            fill="#94a3b8"
          >
            {tx.text}
          </text>
        ))}
      </svg>

      {tip.visible && (
        <div
          className="health-tooltip"
          style={{
            position: "absolute",
            left: tip.x - 60,
            top: tip.y - 76,
            background: "rgba(247,248,249,1)",
            color: "#333",
            padding: "12px 16px",
            borderRadius: "8px",
            fontSize: "13px",
            fontFamily: "Poppins",
            pointerEvents: "none",
            zIndex: 1000,
            boxShadow: "0 6px 20px rgba(0,0,0,0.15)",
            minWidth: 140,
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 4,
              color: "#6B7280",
              fontSize: 11,
              textTransform: "uppercase",
            }}
          >
            {tab.label}
          </div>

          {tip.lines?.map((ln) => (
            <div
              key={ln.label}
              style={{
                fontWeight: 700,
                fontSize: 16,
                lineHeight: 1.2,
                color: ln.color || "#006eea",
              }}
            >
              {ln.label}: {ln.value}
              {ln.unit ? ` ${ln.unit}` : ""}
            </div>
          ))}

          <div style={{ fontSize: 11, marginTop: 6, color: "#64748b" }}>
            {tip.date}
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "-6px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid rgba(247,248,249,1)",
            }}
          />
        </div>
      )}
    </div>
  );
};

const OverviewTabsChart = () => {
  const { t } = useTranslation();

  const userId = useSelector(selectEffectiveUserId);
  const vitalSignals = useSelector(selectVitalSignals);

  const [activeTab, setActiveTab] = useState(0);
  const [activeFilter, setActiveFilter] = useState(0);

  const { getSeries: getBaseSeries, normalZone } = useOverviewSeries(userId);

  const customGroups = useMemo(() => {
    const groups = new Map();

    if (!Array.isArray(vitalSignals)) return groups;

    vitalSignals
      .filter((v) => v?.type === "OTHER")
      .forEach((v) => {
        const label = v.customLabel || "Métrique";
        const unit = v.unit || "";
        const key = makeCustomKey(label, unit);

        if (!groups.has(key)) {
          groups.set(key, {
            key,
            label,
            unit,
            records: [],
          });
        }

        groups.get(key).records.push(v);
      });

    return groups;
  }, [vitalSignals]);

  const customTabs = useMemo(() => {
    return Array.from(customGroups.values())
      .sort((a, b) => a.label.localeCompare(b.label))
      .map((group) => ({
        key: `custom:${encodeURIComponent(group.key)}`,
        label: group.unit ? `${group.label} (${group.unit})` : group.label,
        unit: group.unit,
        isCustom: true,
      }));
  }, [customGroups]);

  const tabsConfig = useMemo(
    () => [
      {
        key: "bloodSugar",
        label: t("overviewTabsChart.tabs.bloodSugar"),
      },
      {
        key: "heartRate",
        label: t("overviewTabsChart.tabs.heartRate"),
      },
      {
        key: "bloodPressure",
        label: t("overviewTabsChart.tabs.bloodPressure"),
      },
      {
        key: "respiratory",
        label: t("overviewTabsChart.tabs.RespiratoryRate"),
      },

      // ✅ Les métriques ajoutées par l'utilisateur
      ...customTabs,
    ],
    [t, customTabs]
  );

  useEffect(() => {
    if (activeTab >= tabsConfig.length) {
      setActiveTab(0);
    }
  }, [activeTab, tabsConfig.length]);

  const getCustomSeries = useCallback(
    (customKey, timeKey) => {
      const group = customGroups.get(customKey);

      if (!group) {
        return {
          labels: [],
          points: [],
          min: 0,
          max: 1,
          count: 0,
        };
      }

      const sorted = [...group.records].sort(
        (a, b) => dateOfMetric(a) - dateOfMetric(b)
      );

      const filtered = filterByTime(sorted, timeKey);

      const valid = filtered
        .map((item) => {
          const value = toNumber(item.value);

          if (value === null) return null;

          return {
            value,
            label: formatDateLabel(item.measuredAt || item.createdAt),
          };
        })
        .filter(Boolean);

      const labels = valid.map((item) => item.label);
      const points = valid.map((item) => item.value);

      if (!points.length) {
        return {
          labels: [],
          points: [],
          min: 0,
          max: 1,
          count: 0,
        };
      }

      const minRaw = Math.min(...points);
      const maxRaw = Math.max(...points);

      const diff = maxRaw - minRaw;
      const padding = diff === 0 ? Math.max(Math.abs(maxRaw) * 0.1, 1) : diff * 0.15;

      return {
        labels,
        points,
        min: minRaw - padding,
        max: maxRaw + padding,
        count: points.length,
      };
    },
    [customGroups]
  );

  const getSeries = useCallback(
    (key, timeKey) => {
      if (key.startsWith("custom:")) {
        const customKey = decodeURIComponent(key.replace("custom:", ""));
        return getCustomSeries(customKey, timeKey);
      }

      return getBaseSeries(key, timeKey);
    },
    [getBaseSeries, getCustomSeries]
  );

  const tab = tabsConfig[activeTab] || tabsConfig[0];
  const timeKey = timeFilters[activeFilter];

  return (
    <section className="otc">
      <div className="otc-head">
        <h3>{t("overviewTabsChart.title")}</h3>

        <div className="otc-filters">
          {timeFilters.map((f, i) => (
            <button
              key={f}
              className={i === activeFilter ? "active" : ""}
              onClick={() => setActiveFilter(i)}
              type="button"
              aria-label={t(`overviewTabsChart.filters.${FILTER_LABEL_KEY[f]}`)}
              title={t(`overviewTabsChart.filters.${FILTER_LABEL_KEY[f]}`)}
            >
              {t(`overviewTabsChart.filters.${FILTER_LABEL_KEY[f]}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="otc-tabs otc-tabs--scroll">
        {tabsConfig.map((tb, i) => (
          <button
            key={tb.key}
            className={`${i === activeTab ? "active" : ""} ${
              tb.isCustom ? "custom-tab" : ""
            }`}
            onClick={() => setActiveTab(i)}
            type="button"
            title={tb.label}
          >
            {tb.isCustom ? " " : ""}
            {tb.label}
          </button>
        ))}
      </div>

      <Chart
        tab={tab}
        timeKey={timeKey}
        getSeries={getSeries}
        normalZone={normalZone}
      />
    </section>
  );
};

export default OverviewTabsChart;